import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "../hooks/useTranslation";
import { getDishAverages } from "../utils/sustainabilityAverages";
import { fetchDishesListThunk } from "../store/slices/dishesReducer";
import {
  fetchCategoriesByAffiliateThunk,
  fetchCategoriesThunk,
  deleteCategoryInStore,
  updateCategoryInStore,
  updateCategoryList,
} from "../store/slices/categoriesReducer";
import {
  deleteCategoryForAffiliate,
  updateCategoryForAffiliate,
  addAffiliateCategory,
} from "../api/api_categories";
import { updateAffiliateImage } from "../api/api_auth";
import CategoryCard from "../components/CategoryCard";
import AddCategoryCard from "../components/AddCategoryCard";
import JourneyBreadcrumb from "../components/navigation/JourneyBreadcrumb";
import TotalScoreGeneral from "../components/TotalScoreGeneral";


const DashboardPage = () => {
  const { t, currentLanguage } = useTranslation();
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  const [deleteError, setDeleteError] = useState("");
  const [editingImageCategoryId, setEditingImageCategoryId] = useState(null);
  const [imageUrlDraft, setImageUrlDraft] = useState("");
  const [savingImage, setSavingImage] = useState(false);
  const [editingUserImage, setEditingUserImage] = useState(false);
  const [userImageUrlDraft, setUserImageUrlDraft] = useState("");
  const [savingUserImage, setSavingUserImage] = useState(false);
  const [isPageLoaded, setIsPageLoaded] = useState(false);

  // Get data from Redux state
  const user = useSelector((state) => state.affiliate.auth);
  const { dishesList } = useSelector((state) => state.dishes);
  const {
    affiliateCategories: categories,
    items,
    status,
    error,
  } = useSelector((state) => state.categories);

  const dishes = useMemo(
    () => (Array.isArray(dishesList.data) ? dishesList.data : []),
    [dishesList.data]
  );
  const loading = dishesList.isLoading;
  const dishesError = dishesList.error;

  const latestDishUpdate = dishes.reduce((latest, dish) => {
    const candidate = dish?.updated_at || dish?.created_at;
    if (!candidate) return latest;
    const candidateMs = new Date(candidate).getTime();
    if (Number.isNaN(candidateMs)) return latest;
    return candidateMs > latest ? candidateMs : latest;
  }, 0);

  const lastUpdatedLabel = latestDishUpdate
    ? new Intl.DateTimeFormat(currentLanguage === "sv" ? "sv-SE" : "en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }).format(new Date(latestDishUpdate))
    : t("Ej angiven");

  useEffect(() => {
    // Check if we have cached data that's less than 2 minutes old
    const cacheAge = dishesList.lastFetched
      ? Date.now() - dishesList.lastFetched
      : null;
    const cacheValid = cacheAge && cacheAge < 120000; // 2 minutes

    // Only fetch if we haven't fetched before OR if cache is invalid
    // Don't refetch just because data.length is 0 (which could be legitimate empty state)
    if (!dishesList.lastFetched || !cacheValid) {
      dispatch(fetchDishesListThunk());
    }

    // Load categories if not already loaded
    if (status === "idle") {
      dispatch(fetchCategoriesByAffiliateThunk());
      dispatch(fetchCategoriesThunk());
    }

    // Set page loaded state for animations
    const timer = setTimeout(() => {
      setIsPageLoaded(true);
    }, 100);
    return () => clearTimeout(timer);
  }, [dispatch, dishesList.lastFetched, status]);

  const getAverageScore = (dish) => {
    const averages = getDishAverages(dish);
    const totalAverage =
      (averages.environment_average +
        averages.health_average +
        averages.social_average) /
      3;
    return Math.round(totalAverage);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "score-excellent";
    if (score >= 60) return "score-good";
    if (score >= 40) return "score-fair";
    return "score-poor";
  };

  const getDishCategoryId = (dish) =>
    Number(dish?.affiliates_category_id ?? dish?.affiliate_category_id ?? 0);

  const getDishCategoryName = (dish) => {
    const categoryId = getDishCategoryId(dish);
    const matched = categories.find((cat) => Number(cat?.id) === categoryId);
    return matched?.name || dish?.category?.name || "Otaggad";
  };

  const dashboardInsights = useMemo(() => {
    const safe = Array.isArray(dishes) ? dishes : [];
    const withMissingImage = safe.filter((dish) => !dish?.image).length;
    const pendingSync = safe.filter((dish) => dish?.has_product_sync_notice).length;
    const lowSustainability = safe.filter((dish) => getAverageScore(dish) < 50).length;

    return {
      withMissingImage,
      pendingSync,
      lowSustainability,
      needsAttention:
        withMissingImage + pendingSync + lowSustainability,
    };
  }, [dishes]);

  // Generate options for the Select component
  const categoriesOption = items
    .filter((item) => {
      if (!item || !item.name) return false;
      const isIdInCategories = categories.some(
        (category) => category.name === item.name
      );
      return !isIdInCategories;
    })
    .map((item) => ({
      value: item.name,
      label: item.name,
    }));

  // Group dishes by category for sustainability calculations
  const categoriesWithSustainability =
    categories?.map((category) => {
      const categoryDishes = dishes.filter(
        (dish) => getDishCategoryId(dish) === Number(category.id)
      );

      let avgSustainability = 0;
      if (categoryDishes.length > 0) {
        const totalScores = categoryDishes.reduce((sum, dish) => {
          const list = Array.isArray(dish?.ingredients) ? dish.ingredients : [];
          if (list.length === 0) return sum;
          const dishScores = list.reduce((dishSum, ingredient) => {
            return (
              dishSum +
                        (ingredient.climate_score || 0) +
          (ingredient.water_score || 0) +
          (ingredient.health_score || 0) +
          (ingredient.social_justice_score || 0)
            );
          }, 0);
          return sum + dishScores / (list.length * 4);
        }, 0);
        avgSustainability = Math.round(totalScores / categoryDishes.length);
      }

      const directImage = category.image_url || category.image || null;
      const categoryImage =
        directImage ||
        (categoryDishes.length > 0 ? categoryDishes[0]?.category?.image : null);

      return {
        ...category,
        avgSustainability,
        dishCount: categoryDishes.length,
        image: categoryImage,
      };
    }) || [];

  // Filter categories based on search term
  const filteredCategories = categoriesWithSustainability.filter((category) =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openDeleteModal = (category) => {
    setSelectedCategory(category);
    setDeleteError("");
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (deleteLoading) return;
    setShowDeleteModal(false);
    setSelectedCategory(null);
    setDeleteError("");
  };

  const handleConfirmDelete = async () => {
    if (!selectedCategory) return;
    setDeleteLoading(true);
    setDeleteError("");
    try {
      await deleteCategoryForAffiliate(selectedCategory.id);
      dispatch(deleteCategoryInStore(selectedCategory.id));
      closeDeleteModal();
    } catch (err) {
      console.error("Failed to delete category", err);
      setDeleteError("Kunde inte radera kategorin. Försök igen.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const openEditImage = (category) => {
    setEditingImageCategoryId(category.id);
    setImageUrlDraft(category.image_url || category.image || "");
  };

  const cancelEditImage = () => {
    if (savingImage) return;
    setEditingImageCategoryId(null);
    setImageUrlDraft("");
  };

  const saveImage = async (category) => {
    if (!editingImageCategoryId || savingImage) return;
    try {
      setSavingImage(true);
      await updateCategoryForAffiliate(category.id, {
        image_url: imageUrlDraft,
      });
      dispatch(
        updateCategoryInStore({
          id: category.id,
          name: category.name,
          image: imageUrlDraft,
        })
      );
      cancelEditImage();
    } catch (err) {
      console.error("Failed to update category image", err);
    } finally {
      setSavingImage(false);
    }
  };

  const openEditUserImage = () => {
    setEditingUserImage(true);
    setUserImageUrlDraft(user?.image || "");
  };

  const cancelEditUserImage = () => {
    if (savingUserImage) return;
    setEditingUserImage(false);
    setUserImageUrlDraft("");
  };

  // Add Category function for flip card
  const handleAddCategory = async (newCategory) => {
    try {
      // Make API call
      const res = await addAffiliateCategory(newCategory);

      // Check response
      const category = res.data.category;
      if (!category) {
        throw new Error("Invalid response structure from API.");
      }

      // Update parent component
      dispatch(updateCategoryList(category));
    } catch (error) {
      console.error("Error Details:", error);
      throw error; // Re-throw so the flip card can handle the error
    }
  };

  const saveUserImage = async () => {
    if (savingUserImage || !userImageUrlDraft) return;
    
    setSavingUserImage(true);
    try {
      // Call the API to update the user image
      await updateAffiliateImage(userImageUrlDraft);
      
      // Refresh the page to get updated user data
      window.location.reload();
      
      setEditingUserImage(false);
      setUserImageUrlDraft("");
    } catch (error) {
      console.error("Failed to update user image:", error);
    } finally {
      setSavingUserImage(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-gray-200 rounded-full animate-pulse"></div>
            <div className="absolute top-0 left-0 w-20 h-20 border-4 border-secondary-green border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="mt-8 text-gray-600 text-lg font-medium animate-pulse-slow">
            Laddar recept...
          </p>
          <div className="mt-6 flex justify-center space-x-2">
            <div className="w-3 h-3 bg-secondary-green rounded-full animate-bounce"></div>
            <div className="w-3 h-3 bg-secondary-green rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-3 h-3 bg-secondary-green rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (dishesError || error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto animate-fade-in">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 transform hover:scale-105 transition-all duration-300 ease-out">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce-in">
              <img src="/assets/icons/Radera.png" alt="Error" className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Fel vid laddning av data
            </h3>
            <p className="text-gray-600 mb-6">{dishesError || error}</p>
            <button
              onClick={() => window.location.reload()}
              className="btn-primary transform hover-lift"
            >
              Försök igen
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Ensure dishes is always an array
  const safeDishes = Array.isArray(dishes) ? dishes : [];

  return (
    <div className={`min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 transition-all duration-500 ease-out ${isPageLoaded ? 'opacity-100' : 'opacity-0'}`}>
      <div className="max-w-7xl mx-auto px-4 tablet:px-6 laptop:px-8 desktop:px-8 py-8">
        <JourneyBreadcrumb
          items={[
            { label: t('Översikt') }
          ]}
          className="mb-6"
        />
        {/* Header */}
        <div className={`mb-6 mobile:mb-8 tablet:mb-8 laptop:mb-8 desktop:mb-8 animate-fade-in-up`}>
          <div className="bg-gray-100 rounded-2xl mobile:rounded-3xl tablet:rounded-3xl laptop:rounded-3xl desktop:rounded-3xl overflow-hidden shadow-sm border-none transition-all duration-300 ease-out hover:shadow-md">
            <div className="relative p-6 mobile:p-8 tablet:p-12 laptop:p-12 desktop:p-12">
              <div className="bg-white rounded-xl mobile:rounded-2xl tablet:rounded-2xl laptop:rounded-2xl desktop:rounded-2xl px-6 mobile:px-8 tablet:px-10 laptop:px-10 desktop:px-10 py-6 mobile:py-8 tablet:py-8 laptop:py-8 desktop:py-8 shadow-sm transition-all duration-300 ease-out hover:shadow-lg">
                <div className="flex items-center mobile:flex-col tablet:flex-row laptop:flex-row desktop:flex-row space-y-4 mobile:space-y-4 tablet:space-y-0 laptop:space-y-0 desktop:space-y-0 gap-6 mobile:gap-6 tablet:gap-8 laptop:gap-8 desktop:gap-8">
                  {/* Affiliate Image */}
                  <div className="relative group">
                    <img
                      src={
                        user?.image ||
                        ""
                      }
                      alt="Affiliate"
                      className="w-24 h-24 mobile:w-32 mobile:h-32 tablet:w-40 tablet:h-40 laptop:w-40 laptop:h-40 desktop:w-40 desktop:h-40 rounded-lg mobile:rounded-xl tablet:rounded-xl laptop:rounded-xl desktop:rounded-xl shadow-sm mx-auto mobile:mx-auto tablet:mx-0 laptop:mx-0 desktop:mx-0 object-cover transition-all duration-300 ease-out group-hover:scale-105 group-hover:shadow-lg transform hover-lift"
                    />

                    {/* Image Edit Overlay */}
                    {editingUserImage && (
                      <div className="absolute inset-0 z-10 bg-white/95 backdrop-blur-sm flex flex-col justify-center p-2 rounded-lg mobile:rounded-xl tablet:rounded-xl laptop:rounded-xl desktop:rounded-xl animate-fade-in">
                        <label className="text-xs font-semibold text-gray-700 mb-2">Ny bild URL</label>
                        <input
                          type="text"
                          value={userImageUrlDraft}
                          onChange={(e) => setUserImageUrlDraft(e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-green focus:border-transparent text-xs bg-white"
                          placeholder="https://..."
                        />
                        <div className="mt-2 flex items-center justify-end space-x-1">
                          <button
                            type="button"
                            onClick={cancelEditUserImage}
                            className="px-2 py-1 text-xs rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                            disabled={savingUserImage}
                          >
                            {t('Avbryt')}
                          </button>
                          <button
                            type="button"
                            onClick={saveUserImage}
                            className="px-2 py-1 text-xs rounded-lg bg-secondary-green text-white hover:opacity-90 disabled:opacity-50"
                            disabled={savingUserImage || !userImageUrlDraft}
                          >
                            {savingUserImage ? 'Sparar...' : 'Spara'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Edit Button */}
                    <button
                      type="button"
                      onClick={openEditUserImage}
                      className="absolute bottom-1 right-1 z-10 inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/80 hover:bg-white/90 border border-gray-200 shadow-sm transition-colors"
                      title="Redigera bild"
                    >
                      <img 
                        src="/assets/icons/Redigera.png" 
                        alt="Edit" 
                        className="w-3 h-3 text-gray-600"
                      />
                    </button>
                  </div>

                  {/* Affiliate Name, Role and Button Block */}
                  <div className="text-center mobile:text-center tablet:text-left laptop:text-left desktop:text-left">
                    <h1 className="text-xl mobile:text-2xl tablet:text-3xl laptop:text-3xl desktop:text-3xl font-bold text-gray-900">
                      {user?.name || "AFFILIATE NAME"}
                    </h1>
                    <p className="text-xs mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm text-gray-600 mt-1 capitalize">
                      {user?.affiliate_role || "User"}
                    </p>
                    {/* CTA Buttons */}
                    <div className="mt-3 mobile:mt-4 tablet:mt-4 laptop:mt-4 desktop:mt-4 flex flex-wrap gap-2 mobile:gap-3 justify-center">
                      <Link
                        to="/menyer"
                        className="inline-block bg-gradient-to-r from-secondary-green to-secondary-green/90 hover:from-secondary-green/90 hover:to-secondary-green text-white font-semibold py-2 mobile:py-3 tablet:py-3 laptop:py-3 desktop:py-3 px-4 mobile:px-6 tablet:px-6 laptop:px-6 desktop:px-6 rounded-lg mobile:rounded-xl tablet:rounded-xl laptop:rounded-xl desktop:rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] text-sm mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm"
                      >
                        {t('Visa alla recept')}
                      </Link>
                      <Link
                        to="/reports"
                        className="inline-block bg-white hover:bg-gray-50 text-gray-700 font-semibold py-2 mobile:py-3 tablet:py-3 laptop:py-3 desktop:py-3 px-4 mobile:px-6 tablet:px-6 laptop:px-6 desktop:px-6 rounded-lg mobile:rounded-xl tablet:rounded-xl laptop:rounded-xl desktop:rounded-xl border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] text-sm mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm"
                      >
                        {t('Visa rapporter')}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 mobile:grid-cols-2 tablet:grid-cols-2 laptop:grid-cols-4 desktop:grid-cols-4 gap-4 mobile:gap-4 tablet:gap-6 laptop:gap-6 desktop:gap-6 mb-6 mobile:mb-8 tablet:mb-8 laptop:mb-8 desktop:mb-8">
              <div className="bg-white rounded-xl mobile:rounded-2xl tablet:rounded-2xl laptop:rounded-2xl desktop:rounded-2xl shadow-sm border border-gray-100 p-4 mobile:p-6 tablet:p-6 laptop:p-6 desktop:p-6 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 mobile:w-12 mobile:h-12 tablet:w-12 tablet:h-12 laptop:w-12 laptop:h-12 desktop:w-12 desktop:h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg mobile:rounded-xl tablet:rounded-xl laptop:rounded-xl desktop:rounded-xl flex items-center justify-center">
                      <img src="/assets/icons/Innehall.png" alt="Recipes" className="w-5 h-5 mobile:w-6 mobile:h-6 tablet:w-6 tablet:h-6 laptop:w-6 laptop:h-6 desktop:w-6 desktop:h-6 text-blue-600" />
                    </div>
                  </div>
                  <div className="ml-3 mobile:ml-4 tablet:ml-4 laptop:ml-4 desktop:ml-4">
                    <p className="text-xs mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm font-medium text-gray-500">
                      {t('Totalt recept')}
                    </p>
                    <p className="text-2xl mobile:text-3xl tablet:text-3xl laptop:text-3xl desktop:text-3xl font-bold text-gray-900">
                      {safeDishes.length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl mobile:rounded-2xl tablet:rounded-2xl laptop:rounded-2xl desktop:rounded-2xl shadow-sm border border-gray-100 p-4 mobile:p-6 tablet:p-6 laptop:p-6 desktop:p-6 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 mobile:w-12 mobile:h-12 tablet:w-12 tablet:h-12 laptop:w-12 laptop:h-12 desktop:w-12 desktop:h-12 bg-gradient-to-br from-secondary-green/20 to-secondary-green/30 rounded-lg mobile:rounded-xl tablet:rounded-xl laptop:rounded-xl desktop:rounded-xl flex items-center justify-center">
                      <img src="/assets/icons/Miljo.png" alt="Sustainability" className="w-5 h-5 mobile:w-6 mobile:h-6 tablet:w-6 tablet:h-6 laptop:w-6 laptop:h-6 desktop:w-6 desktop:h-6 text-secondary-green" />
                    </div>
                  </div>
                  <div className="ml-3 mobile:ml-4 tablet:ml-4 laptop:ml-4 desktop:ml-4">
                    <p className="text-xs mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm font-medium text-gray-500">
                      {t('Genomsnittlig hållbarhet')}
                    </p>
                    <p className="text-2xl mobile:text-3xl tablet:text-3xl laptop:text-3xl desktop:text-3xl font-bold text-gray-900">
                      {safeDishes.length > 0
                        ? Math.round(
                            safeDishes.reduce(
                              (sum, dish) => sum + getAverageScore(dish),
                              0
                            ) / safeDishes.length
                          )
                        : 0}
                      
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl mobile:rounded-2xl tablet:rounded-2xl laptop:rounded-2xl desktop:rounded-2xl shadow-sm border border-gray-100 p-4 mobile:p-6 tablet:p-6 laptop:p-6 desktop:p-6 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 mobile:w-12 mobile:h-12 tablet:w-12 tablet:h-12 laptop:w-12 laptop:h-12 desktop:w-12 desktop:h-12 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-lg mobile:rounded-xl tablet:rounded-xl laptop:rounded-xl desktop:rounded-xl flex items-center justify-center">
                      <img src="/assets/icons/Check.png" alt="Clock" className="w-5 h-5 mobile:w-6 mobile:h-6 tablet:w-6 tablet:h-6 laptop:w-6 laptop:h-6 desktop:w-6 desktop:h-6 text-yellow-600" />
                    </div>
                  </div>
                  <div className="ml-3 mobile:ml-4 tablet:ml-4 laptop:ml-4 desktop:ml-4">
                    <p className="text-xs mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm font-medium text-gray-500">
                      {t('Senast uppdaterad')}
                    </p>
                    <p className="text-2xl mobile:text-3xl tablet:text-3xl laptop:text-3xl desktop:text-3xl font-bold text-gray-900">
                      {lastUpdatedLabel}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl mobile:rounded-2xl tablet:rounded-2xl laptop:rounded-2xl desktop:rounded-2xl shadow-sm border border-gray-100 p-4 mobile:p-6 tablet:p-6 laptop:p-6 desktop:p-6 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 mobile:w-12 mobile:h-12 tablet:w-12 tablet:h-12 laptop:w-12 laptop:h-12 desktop:w-12 desktop:h-12 bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg mobile:rounded-xl tablet:rounded-xl laptop:rounded-xl desktop:rounded-xl flex items-center justify-center">
                      <img src="/assets/icons/Innehall.png" alt="Improvements" className="w-5 h-5 mobile:w-6 mobile:h-6 tablet:w-6 tablet:h-6 laptop:w-6 laptop:h-6 desktop:w-6 desktop:h-6 text-purple-600" />
                    </div>
                  </div>
                  <div className="ml-3 mobile:ml-4 tablet:ml-4 laptop:ml-4 desktop:ml-4">
                    <p className="text-xs mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm font-medium text-gray-500">
                      {t('Kräver åtgärd')}
                    </p>
                    <p className="text-2xl mobile:text-3xl tablet:text-3xl laptop:text-3xl desktop:text-3xl font-bold text-amber-600">
                      {dashboardInsights.needsAttention}
                    </p>
                  </div>
                </div>
              </div>
            </div>

        {/* Categories Section */}
        <div className="bg-white rounded-xl mobile:rounded-2xl tablet:rounded-2xl laptop:rounded-2xl desktop:rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-8">
          <div className="flex items-center justify-between p-4 mobile:p-6 tablet:p-6 laptop:p-6 desktop:p-6 border-b border-gray-100">
            <h2 className="text-xl mobile:text-2xl tablet:text-2xl laptop:text-2xl desktop:text-2xl font-bold text-gray-900">
              {t('Receptkategorier')}
            </h2>
            <div className="flex items-center space-x-4">
              {/* Search */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <img src="/assets/icons/Filter.png" alt="Search" className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder={t("Sök kategorier...")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-green focus:border-transparent transition-all duration-200 bg-white shadow-sm text-sm"
                />
              </div>
            </div>
          </div>

          <div className="p-4 mobile:p-6 tablet:p-8 laptop:p-8 desktop:p-8">
            {filteredCategories.length === 0 && searchTerm ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <img src="/assets/icons/Innehall.png" alt="No categories" className="h-10 w-10 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t("Inga kategorier hittades")}
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  {t("Prova att justera dina söktermer.")}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 mobile:grid-cols-1 tablet:grid-cols-2 laptop:grid-cols-3 desktop:grid-cols-4 gap-4 mobile:gap-4 tablet:gap-6 laptop:gap-6 desktop:gap-6">
                {filteredCategories.map((category) => (
                  <CategoryCard
                    key={category.id}
                    category={category}
                    editingImageCategoryId={editingImageCategoryId}
                    imageUrlDraft={imageUrlDraft}
                    savingImage={savingImage}
                    onEditImage={openEditImage}
                    onCancelEditImage={cancelEditImage}
                    onSaveImage={saveImage}
                    onSetImageUrlDraft={setImageUrlDraft}
                    onDeleteCategory={openDeleteModal}
                    getScoreColor={getScoreColor}
                  />
                ))}

                {/* Add New Category Card - Always last in grid */}
                <div id="add-category-card" className="h-full">
                  <AddCategoryCard 
                    categoriesOption={categoriesOption} 
                    onAddCategory={handleAddCategory}
                  />
                </div>
              </div>
            )}
          </div>

        </div>

        {/* Recent Recipes */}
        <div className="bg-white rounded-xl mobile:rounded-2xl tablet:rounded-2xl laptop:rounded-2xl desktop:rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-8">
          <div className="flex items-center justify-between p-4 mobile:p-6 tablet:p-6 laptop:p-6 desktop:p-6 border-b border-gray-100">
            <h2 className="text-xl mobile:text-2xl tablet:text-2xl laptop:text-2xl desktop:text-2xl font-bold text-gray-900">
              {t('Senaste recept')}
            </h2>
          </div>

          {safeDishes.length === 0 ? (
            <div className="text-center py-12 mobile:py-16 tablet:py-16 laptop:py-16 desktop:py-16">
              <div className="w-20 h-20 mobile:w-24 mobile:h-24 tablet:w-24 tablet:h-24 laptop:w-24 laptop:h-24 desktop:w-24 desktop:h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 mobile:mb-6 tablet:mb-6 laptop:mb-6 desktop:mb-6">
                <img
                  src="/assets/icons/Innehall.png"
                  alt="No Recipes"
                  className="h-10 w-10 mobile:h-12 mobile:w-12 tablet:h-12 tablet:w-12 laptop:h-12 laptop:w-12 desktop:h-12 desktop:w-12 text-gray-400"
                />
              </div>
              <h3 className="text-lg mobile:text-xl tablet:text-xl laptop:text-xl desktop:text-xl font-semibold text-gray-900 mb-2">
                {t("Inga recept än")}
              </h3>
              <p className="text-sm mobile:text-base tablet:text-base laptop:text-base desktop:text-base text-gray-600 mb-6 mobile:mb-8 tablet:mb-8 laptop:mb-8 desktop:mb-8">
                {t("Börja skapa ditt första recept.")}
              </p>
              <Link
                to="/menyer"
                className="bg-gradient-to-r from-secondary-green to-secondary-green/90 hover:from-secondary-green/90 hover:to-secondary-green text-white font-semibold py-2 mobile:py-3 tablet:py-3 laptop:py-3 desktop:py-3 px-4 mobile:px-6 tablet:px-6 laptop:px-6 desktop:px-6 rounded-lg mobile:rounded-xl tablet:rounded-xl laptop:rounded-xl desktop:rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] text-sm mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm"
              >
                {t('Skapa recept')}
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 mobile:px-4 tablet:px-6 laptop:px-6 desktop:px-6 py-3 mobile:py-4 tablet:py-4 laptop:py-4 desktop:py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t('Recept')}
                    </th>
                    <th className="hidden mobile:hidden tablet:table-cell laptop:table-cell desktop:table-cell px-3 mobile:px-4 tablet:px-6 laptop:px-6 desktop:px-6 py-3 mobile:py-4 tablet:py-4 laptop:py-4 desktop:py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t('Kategori')}
                    </th>
                    <th className="hidden mobile:hidden tablet:table-cell laptop:table-cell desktop:table-cell px-3 mobile:px-4 tablet:px-6 laptop:px-6 desktop:px-6 py-3 mobile:py-4 tablet:py-4 laptop:py-4 desktop:py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t("Hållbarhetspoäng")}
                    </th>
                    <th className="hidden mobile:hidden tablet:hidden laptop:table-cell desktop:table-cell px-3 mobile:px-4 tablet:px-6 laptop:px-6 desktop:px-6 py-3 mobile:py-4 tablet:py-4 laptop:py-4 desktop:py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t("Detaljerade Poäng")}
                    </th>
                    <th className="hidden mobile:hidden tablet:hidden laptop:table-cell desktop:table-cell px-3 mobile:px-4 tablet:px-6 laptop:px-6 desktop:px-6 py-3 mobile:py-4 tablet:py-4 laptop:py-4 desktop:py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t("Ingredienser")}
                    </th>
                    <th className="px-3 mobile:px-4 tablet:px-6 laptop:px-6 desktop:px-6 py-3 mobile:py-4 tablet:py-4 laptop:py-4 desktop:py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t("Åtgärder")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {safeDishes.slice(0, 5).map((dish) => {
                    const avgScore = getAverageScore(dish);
                    return (
                      <tr
                        key={dish.id}
                        className="hover:bg-gray-50 transition-colors duration-150"
                      >
                        <td className="px-3 mobile:px-4 tablet:px-6 laptop:px-6 desktop:px-6 py-3 mobile:py-4 tablet:py-4 laptop:py-4 desktop:py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 mobile:h-12 mobile:w-12 tablet:h-12 tablet:w-12 laptop:h-12 laptop:w-12 desktop:h-12 desktop:w-12">
                              {dish.image ? (
                                <img
                                  className="h-10 w-10 mobile:h-12 mobile:w-12 tablet:h-12 tablet:w-12 laptop:h-12 laptop:w-12 desktop:h-12 desktop:w-12 rounded-lg mobile:rounded-xl tablet:rounded-xl laptop:rounded-xl desktop:rounded-xl object-cover shadow-sm"
                                  src={dish.image}
                                  alt={dish.name}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                    e.target.nextSibling.style.display = "flex";
                                  }}
                                />
                              ) : null}
                              <div
                                className="h-10 w-10 mobile:h-12 mobile:w-12 tablet:h-12 tablet:w-12 laptop:h-12 laptop:w-12 desktop:h-12 desktop:w-12 rounded-lg mobile:rounded-xl tablet:rounded-xl laptop:rounded-xl desktop:rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center shadow-sm"
                                style={{
                                  display: dish.image ? "none" : "flex",
                                }}
                              >
                                <img src="/assets/icons/Innehall.png" alt="Innehall icon" className="h-5 w-5 mobile:h-6 mobile:w-6 tablet:h-6 tablet:w-6 laptop:h-6 laptop:w-6 desktop:h-6 desktop:w-6 text-gray-400" />
                              </div>
                            </div>
                            <div className="ml-3 mobile:ml-4 tablet:ml-4 laptop:ml-4 desktop:ml-4">
                              <div
                                className="text-sm mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm font-semibold text-gray-900 max-w-[260px] truncate"
                                title={dish.name || ""}
                              >
                                {(dish.name || "").length > 40
                                  ? `${String(dish.name).slice(0, 40)}...`
                                  : dish.name}
                              </div>
                              <div className="mobile:block tablet:hidden laptop:hidden desktop:hidden">
                                <div className="text-xs text-gray-500">
                                  {t(getDishCategoryName(dish))}
                                </div>
                                <div className="text-xs text-gray-500">
                                  Hållbarhet: {avgScore}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  {(() => {
                                    const a = getDishAverages(dish);
                                    return `Miljö: ${a.environment_average} | Hälsa: ${a.health_average} | Social: ${a.social_average}`;
                                  })()}
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="hidden mobile:hidden tablet:table-cell laptop:table-cell desktop:table-cell px-3 mobile:px-4 tablet:px-6 laptop:px-6 desktop:px-6 py-3 mobile:py-4 tablet:py-4 laptop:py-4 desktop:py-4 whitespace-nowrap">
                          <div className="text-sm mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm text-gray-900">
                            {t(getDishCategoryName(dish))}
                          </div>
                        </td>
                        <td className="hidden mobile:hidden tablet:table-cell laptop:table-cell desktop:table-cell px-3 mobile:px-4 tablet:px-6 laptop:px-6 desktop:px-6 py-3 mobile:py-4 tablet:py-4 laptop:py-4 desktop:py-4 whitespace-nowrap">
                          <TotalScoreGeneral score={avgScore} size="xxs" />
                          
                        </td>
                        <td className="hidden mobile:hidden tablet:hidden laptop:table-cell desktop:table-cell px-3 mobile:px-4 tablet:px-6 laptop:px-6 desktop:px-6 py-3 mobile:py-4 tablet:py-4 laptop:py-4 desktop:py-4">
                          <div className="flex flex-col space-y-1">
                            <div className="text-xs text-gray-500">
                              {(() => {
                                const avg = getDishAverages(dish);
                                return (
                                  <>
                                    <span className="text-secondary-climateGreen">{t("Miljö:")}</span> {avg.environment_average} | <span className="text-secondary-healthPink">{t("Hälsa:")}</span> {avg.health_average} | <span className="text-secondary-socialOrange">{t("Rättvisa:")}</span> {avg.social_average}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </td>
                        <td className="hidden mobile:hidden tablet:hidden laptop:table-cell desktop:table-cell px-3 mobile:px-4 tablet:px-6 laptop:px-6 desktop:px-6 py-3 mobile:py-4 tablet:py-4 laptop:py-4 desktop:py-4 whitespace-nowrap text-sm mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm text-gray-500 lowercase">
                          {Array.isArray(dish.ingredients)
                            ? dish.ingredients.length
                            : 0}{" "}
                           {t('Ingredienser')}
                        </td>
                        <td className="px-3 mobile:px-4 tablet:px-6 laptop:px-6 desktop:px-6 py-3 mobile:py-4 tablet:py-4 laptop:py-4 desktop:py-4 whitespace-nowrap text-sm mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm font-medium">
                          <Link
                            to={`/items/${dish.id}`}
                            className="text-secondary-green hover:text-secondary-green/80 font-semibold transition-colors duration-200"
                          >
                            {t('Visa detaljer')}
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-50">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/40"
              onClick={closeDeleteModal}
            />
            {/* Modal Panel */}
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">
                    {t('Radera kategori')}
                  </h3>
                  <button
                    className="inline-flex items-center justify-center w-9 h-9 rounded-lg hover:bg-gray-100"
                    onClick={closeDeleteModal}
                    aria-label={t("Stäng")}
                  >
                    <img src="/assets/icons/Dolj.png" alt="Close" className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
                <div className="px-5 pt-4 pb-2">
                  <p className="text-sm text-gray-700">
                    {t("Är du säker på att du vill radera kategorin")}
                    {selectedCategory ? ` "${selectedCategory.name}"` : ""}?
                    {t("Alla dess recept kommer också att raderas.")}
                  </p>
                  {deleteError && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                      {deleteError}
                    </div>
                  )}
                </div>
                <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end space-x-3">
                  <button
                    className="bg-white hover:bg-gray-50 text-gray-700 font-semibold py-2 px-4 rounded-lg border border-gray-200 transition-all duration-200"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      closeDeleteModal();
                    }}
                    disabled={deleteLoading}
                  >
                    Avbryt {t('Avbryt')}
                  </button>
                  <button
                    className="bg-gradient-to-r from-red-600 to-red-600/90 hover:from-red-600/90 hover:to-red-600 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 disabled:opacity-50"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleConfirmDelete();
                    }}
                    disabled={deleteLoading}
                  >
                    {deleteLoading ? t('Raderar...') : t('Radera')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default DashboardPage;
